import { Message } from 'dg-web-shared/lib/Localized.ts';
import { LicensePlateType } from 'dg-web-shared/dto/LicensePlateType.ts';
import { ZoneType } from 'dg-web-shared/dto/ZoneType.ts';

import {
    GuestPassDurationCalculationMode,
    PurchaseVisibility,
} from './ProductTemplateEnums.ts';
import { SubscriptionIntervalConfig } from './SubscriptionIntervalConfig.ts';
import { ReservationTimeAndPriceConfig } from './ReservationTimeAndPriceConfig.ts';
import { FixedDuration } from './FixedDuration.tsx';

export const enum ProductTemplateConfigType {
    ALL_ACCESS = 'ALL_ACCESS',
    CALENDAR_DAY_RESERVATION = 'CALENDAR_DAY_RESERVATION',
    CUSTOMER_EXTERNAL = 'CUSTOMER_EXTERNAL',
    EVENT = 'EVENT',
    GUEST_PERMIT_VALID_TO = 'GUEST_PERMIT_VALID_TO',
    GUEST_RESERVATION = 'GUEST_RESERVATION',
    GUEST_RESERVATION_UBS = 'GUEST_RESERVATION_UBS',
    GUEST_DAY_PASS = 'GUEST_DAY_PASS',
    PERMIT_FREE_FIXED_DURATION = 'PERMIT_FREE_FIXED_DURATION',
    PERMIT_FREE_VALID_TO = 'PERMIT_FREE_VALID_TO',
    PERMIT_UNTIL_REVOCATION = 'PERMIT_UNTIL_REVOCATION',
    SUBSCRIPTION = 'SUBSCRIPTION',
}

export type ProductTemplate =
    | AllAccess
    | CalendarDayReservation
    | Event
    | ExternalCustomer
    | GuestPermitValidTo
    | GuestReservationUbs
    | PermitFreeFixedDuration
    | PermitFreeValidTo
    | PermitUntilRevocation
    | Subscription;

type ProductTemplateBase = {
    contractTemplateId: number;
    productTemplateVersionId: string;
    operatorId: number;
    dtaAccountId: number;
    config: ProductTemplateConfig;
};

export type PermitUntilRevocation = {
    readonly config: PermitUntilRevocationConfig;
} & ProductTemplateBase;

export type PermitFreeFixedDuration = {
    readonly config: PermitFreeFixedDurationConfig;
} & ProductTemplateBase;

export type Subscription = {
    readonly config: SubscriptionConfig;
} & ProductTemplateBase;

export type CalendarDayReservation = {
    readonly config: CalendarDayReservationConfig;
} & ProductTemplateBase;

export type GuestReservationUbs = {
    readonly config: GuestReservationUbsConfig;
} & ProductTemplateBase;

export type AllAccess = {
    readonly config: AllAccessConfig;
} & ProductTemplateBase;

export type GuestPermitValidTo = {
    readonly config: GuestPermitValidToConfig;
} & ProductTemplateBase;

export type GuestReservation = {
    readonly config: GuestReservationConfig;
} & ProductTemplateBase;

export type GuestDayPass = {
    readonly config: GuestDayPassConfig;
} & ProductTemplateBase;

export type ExternalCustomer = {
    readonly config: ExternalCustomerConfig;
} & ProductTemplateBase;

export type Event = {
    readonly config: EventConfig;
} & ProductTemplateBase;

export type PermitFreeValidTo = {
    readonly config: PermitFreeValidToConfig;
} & ProductTemplateBase;

export type ProductTemplateConfig =
    | AllAccessConfig
    | CalendarDayReservationConfig
    | EventConfig
    | ExternalCustomerConfig
    | GuestPermitValidToConfig
    | GuestReservationConfig
    | GuestReservationUbsConfig
    | GuestDayPassConfig
    | PermitFreeFixedDurationConfig
    | PermitFreeValidToConfig
    | PermitUntilRevocationConfig
    | SubscriptionConfig;

interface ProductTemplateBaseConfig {
    configType: ProductTemplateConfigType;
    productName: Message;
    navProjectNumber: string;
    additionalInfo: Message | null;
    vehicleTypes: LicensePlateType[];
    allowedZoneTypes: ZoneType[];
}

interface ParkingaboProductTemplateBaseConfig extends ProductTemplateBaseConfig {
    appPurchaseMode: AppPurchaseMode;
    operatorPurchaseMode: OperatorPurchaseMode;
    parkingaboRoleConfig: ParkingaboRoleConfig;
}

export interface PermitUntilRevocationConfig extends ParkingaboProductTemplateBaseConfig {
    appPurchaseMode: NoAppPurchase;
    operatorPurchaseMode: OperatorPurchaseConfigurable;
    configType: ProductTemplateConfigType.PERMIT_UNTIL_REVOCATION;
    maxConcurrentVehiclesLimitConfig: MaxConcurrentVehiclesLimitConfig;
}

export interface PermitFreeFixedDurationConfig extends ParkingaboProductTemplateBaseConfig {
    appPurchaseMode: NoAppPurchase;
    operatorPurchaseMode: OperatorPurchaseConfigurable;
    configType: ProductTemplateConfigType.PERMIT_FREE_FIXED_DURATION;
    allowedZoneTypes: [ZoneType.barrier_gate];
    allowedDurations: FixedDuration[];
    maxConcurrentVehiclesLimitConfig: MaxConcurrentVehiclesLimitConfig;
}

export interface GuestPermitValidToConfig extends ProductTemplateBaseConfig {
    configType: ProductTemplateConfigType.GUEST_PERMIT_VALID_TO;
    allowedZoneTypes: [ZoneType.barrier_gate];
}

export interface GuestReservationConfig extends ProductTemplateBaseConfig {
    configType: ProductTemplateConfigType.GUEST_RESERVATION;
    defaultDurationHours: number;
    maxDurationHours: number;
    maxDaysPurchaseAheadInFuture: number;
    allowedZoneTypes: [ZoneType.barrier_gate];
}

export interface GuestDayPassConfig extends ProductTemplateBaseConfig {
    configType: ProductTemplateConfigType.GUEST_DAY_PASS;
    allowedZoneTypes: [ZoneType.barrier_gate];
    durationDayOptions: number[];
    durationCalculationMode: GuestPassDurationCalculationMode;
    allowFirstExitWithoutEntry: boolean;
}

export interface AllAccessConfig extends ParkingaboProductTemplateBaseConfig {
    appPurchaseMode: NoAppPurchase;
    operatorPurchaseMode: OperatorPurchaseConfigurable;
    configType: ProductTemplateConfigType.ALL_ACCESS;
    allowedZoneTypes: [ZoneType.barrier_gate];
}

export interface ExternalCustomerConfig extends ProductTemplateBaseConfig {
    configType: ProductTemplateConfigType.CUSTOMER_EXTERNAL;
    allowedZoneTypes: [ZoneType.barrier_gate];
    productReferences: string[];
}

export interface EventConfig extends ProductTemplateBaseConfig {
    configType: ProductTemplateConfigType.EVENT;
    allowedZoneTypes: [ZoneType.barrier_gate];
}

export interface PermitFreeValidToConfig extends ParkingaboProductTemplateBaseConfig {
    appPurchaseMode: NoAppPurchase;
    operatorPurchaseMode: OperatorPurchaseConfigurable;
    configType: ProductTemplateConfigType.PERMIT_FREE_VALID_TO;
    maxConcurrentVehiclesLimitConfig: MaxConcurrentVehiclesLimitConfig;
}

export interface SubscriptionConfig extends ParkingaboProductTemplateBaseConfig {
    configType: ProductTemplateConfigType.SUBSCRIPTION;
    intervalConfig: SubscriptionIntervalConfig;
    maxConcurrentVehiclesLimitConfig: MaxConcurrentVehiclesLimitConfig;
}

export interface CalendarDayReservationConfig extends ParkingaboProductTemplateBaseConfig {
    configType: ProductTemplateConfigType.CALENDAR_DAY_RESERVATION;
    appPurchaseMode: AppPurchaseConfigurable;
    operatorPurchaseMode: NoOperatorPurchase;
    timeAndPriceConfig: ReservationTimeAndPriceConfig;
    maxConcurrentVehiclesLimitConfig: MaxConcurrentVehiclesLimitConfig;
}

export interface GuestReservationUbsConfig extends ParkingaboProductTemplateBaseConfig {
    configType: ProductTemplateConfigType.GUEST_RESERVATION_UBS;
    appPurchaseMode: AppPurchaseConfigurable;
    operatorPurchaseMode: NoOperatorPurchase;
    appPurchaseConfig: GuestReservationUbsAppPurchaseConfig;
    maxHours: number;
    maxDaysPurchaseAheadInFuture: number;
    minValidityStart: string | null;
    maxValidityEnd: string | null;
    maxConcurrentVehiclesLimitConfig: MaxConcurrentVehiclesLimitConfig;
}

export interface GuestReservationUbsAppPurchaseConfig {
    disclaimerText: Message;
    customerRemarkLabel: Message | null;
    customerRemarkRequired: boolean;
}

export type MaxConcurrentVehiclesLimitConfig =
    | UnlimitedVehiclesLimitConfig
    | RangedVehiclesLimitConfig;

type UnlimitedVehiclesLimitConfig = Record<string, undefined>;

export type ParkingaboRoleConfig =
    | ParkingaboRoleConfigAllRoles
    | ParkingaboRoleConfigRestrictedRoles;

type ParkingaboRoleConfigAllRoles = Record<string, undefined>;

export type ParkingaboRoleConfigRestrictedRoles = {
    restrictedToRoleIds: string[];
};

export type RangedVehiclesLimitConfig = {
    minVehicleSelection: number;
    maxVehicleSelection: number;
};

export type FreeOfChargeProductTemplateConfig =
    | PermitUntilRevocationConfig
    | PermitFreeFixedDurationConfig
    | AllAccessConfig
    | PermitFreeValidToConfig;

export function isRangedVehicleLimit(
    vehicleLimitConfig: MaxConcurrentVehiclesLimitConfig,
): vehicleLimitConfig is RangedVehiclesLimitConfig {
    return Boolean(vehicleLimitConfig?.minVehicleSelection);
}

export function isParkingaboRoleConfigAllRoles(
    parkingaboRoleConfig: ParkingaboRoleConfig,
): parkingaboRoleConfig is ParkingaboRoleConfigAllRoles {
    return !Array.isArray(
        (parkingaboRoleConfig as ParkingaboRoleConfigRestrictedRoles)
            .restrictedToRoleIds,
    );
}

export function reduceParkingaboRoleConfig(
    config: ParkingaboRoleConfig,
): ParkingaboRoleConfigRestrictedRoles | null {
    return isParkingaboRoleConfigAllRoles(config) ? null : config;
}

export function isParkingaboProductTemplateConfigType(
    configType: ProductTemplateConfigType,
): boolean {
    switch (configType) {
        case ProductTemplateConfigType.PERMIT_UNTIL_REVOCATION:
        case ProductTemplateConfigType.PERMIT_FREE_FIXED_DURATION:
        case ProductTemplateConfigType.SUBSCRIPTION:
        case ProductTemplateConfigType.CALENDAR_DAY_RESERVATION:
        case ProductTemplateConfigType.GUEST_RESERVATION_UBS:
        case ProductTemplateConfigType.ALL_ACCESS:
        case ProductTemplateConfigType.PERMIT_FREE_VALID_TO:
            return true;
        case ProductTemplateConfigType.GUEST_PERMIT_VALID_TO:
        case ProductTemplateConfigType.GUEST_RESERVATION:
        case ProductTemplateConfigType.CUSTOMER_EXTERNAL:
        case ProductTemplateConfigType.EVENT:
        case ProductTemplateConfigType.GUEST_DAY_PASS:
            return false;
    }
}

export type AppPurchaseMode = AppPurchaseConfigurable | NoAppPurchase;

type NoAppPurchase = Record<string, undefined>;

export type AppPurchaseConfigurable = {
    appPurchaseVisibility: PurchaseVisibility;
};

export type OperatorPurchaseMode =
    | OperatorPurchaseConfigurable
    | NoOperatorPurchase;

type NoOperatorPurchase = Record<string, undefined>;

export type OperatorPurchaseConfigurable = {
    operatorPurchaseVisibility: PurchaseVisibility;
};
